import React, { useEffect, useState } from "react"; 
import { useDispatch, useSelector } from "react-redux";
import { getSchoolBySchoolID } from "../../../services/operations/schoolAPI";
import { getSubscriptionById } from "../../../services/operations/subscriptionsAPI";
import { getUserById } from "../../../services/operations/authAPI";
import { useTranslation } from "react-i18next";

const Subscription = () => {
  const { t } = useTranslation();
  const { user } = useSelector((state) => state.profile);
  const dispatch = useDispatch();

  const [school, setSchool] = useState(null);
  const [subscription, setSubscription] = useState(null);

  const [userNames, setUserNames] = useState({
    admins: [],
    teachers: [],
    parents: [],
    students: [],
  });

  const [activatedByName, setActivatedByName] = useState("");

  const [showAll, setShowAll] = useState({
    admins: false,
    teachers: false,
    parents: false,
    students: false,
  });

  const resolveName = async (value) => {
    try {
      if (!value) return t("adminSubscription.unknown");
      if (typeof value === "object" && value.name) return value.name;
      const res = await dispatch(getUserById(value));
      return res?.name || t("adminSubscription.unknown");
    } catch {
      return t("adminSubscription.unknown");
    }
  };

  const fetchNamesArray = async (ids) => {
    return Promise.all(
      (ids || []).map(async (id) => {
        return await resolveName(id);
      })
    );
  };

  useEffect(() => {
    const fetchSchoolData = async () => {
      if (!user?.schoolId) return;

      try {
        const res = await getSchoolBySchoolID(user.schoolId);
        setSchool(res);

        if (res?.activeSubscriptionId) {
          const subRes = await getSubscriptionById(res.activeSubscriptionId);
          setSubscription(subRes);
          const actName = await resolveName(subRes?.activatedBy);
          setActivatedByName(actName);
        }

        const admins = await fetchNamesArray(res.admins);
        const teachers = await fetchNamesArray(res.teachers);
        const parents = await fetchNamesArray(res.parents);
        const students = await fetchNamesArray(res.students);

        setUserNames({ admins, teachers, parents, students });
      } catch (err) {
        console.error("FETCH SCHOOL ERROR:", err);
      }
    };

    fetchSchoolData();
  }, [user, dispatch]);

  if (!user) return <div className="flex items-center justify-center h-screen"><p>{t("adminSubscription.loading")}</p></div>;
  if (!school) return <div className="flex items-center justify-center h-screen"><p>{t("adminSubscription.noSchoolData")}</p></div>;

  const renderUserList = (list, type) => {
    const limit = 5;
    const show = showAll[type];
    const displayed = show ? list : list.slice(0, limit);

    return (
      <div>
        {displayed.length ? displayed.join(", ") : "-"}
        {list.length > limit && (
          <button
            className="ml-2 text-blue-500 underline"
            onClick={() => setShowAll({ ...showAll, [type]: !show })}
          >
            {show ? t("adminSubscription.seeLess") : t("adminSubscription.seeMore")}
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      <h1 className="text-2xl font-bold text-[#00254D]">{t("adminSubscription.schoolDetails")}</h1>

      {/* School Info */}
      <div className="bg-white shadow-md rounded-lg p-6 border border-gray-200 space-y-2">
        <p><span className="font-semibold">{t("adminSubscription.name")}: </span>{school.name}</p>
        <p><span className="font-semibold">{t("adminSubscription.email")}: </span>{school.email}</p>
        <p><span className="font-semibold">{t("adminSubscription.address")}: </span>{school.address}</p>
        <p><span className="font-semibold">{t("adminSubscription.subscriptionStatus")}: </span>{school.subscriptionStatus}</p>

        {subscription ? (
          <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200 space-y-1">
            <p><span className="font-semibold">{t("adminSubscription.plan")}: </span>{subscription.plan}</p>
            <p><span className="font-semibold">{t("adminSubscription.startDate")}: </span>{new Date(subscription.startDate).toLocaleDateString()}</p>
            <p><span className="font-semibold">{t("adminSubscription.endDate")}: </span>{new Date(subscription.endDate).toLocaleDateString()}</p>
            <p><span className="font-semibold">{t("adminSubscription.activeStatus")}: </span>{subscription.isActive ? t("adminSubscription.active") : t("adminSubscription.expired")}</p>
            <p><span className="font-semibold">{t("adminSubscription.activatedBy")}: </span>{activatedByName}</p>
            <p><span className="font-semibold">{t("adminSubscription.apiLimit")}: </span>{subscription.apiLimitPerDay}</p>
            <p><span className="font-semibold">{t("adminSubscription.apiCallsToday")}: </span>{subscription.apiCallsToday}</p>
          </div>
        ) : (
          <p className="text-gray-500">{t("adminSubscription.noSubscription")}</p>
        )}

        <p><span className="font-semibold">{t("adminSubscription.createdAt")}: </span>{new Date(school.createdAt).toLocaleString()}</p>
        <p><span className="font-semibold">{t("adminSubscription.updatedAt")}: </span>{new Date(school.updatedAt).toLocaleString()}</p>
      </div>

      {/* Users Info */}
      <div className="bg-white shadow-md rounded-lg p-6 border border-gray-200 space-y-4">
        <h2 className="text-xl font-semibold">
          {t("adminSubscription.users")} (
          {userNames.admins.length +
            userNames.teachers.length +
            userNames.parents.length +
            userNames.students.length}
          )
        </h2>
        <p>
          <span className="font-semibold">{t("adminSubscription.admins")}({userNames.admins.length}) </span>
          {renderUserList(userNames.admins, "admins")}
        </p>
        <p>
          <span className="font-semibold">{t("adminSubscription.teachers")}({userNames.teachers.length}) </span>
          {renderUserList(userNames.teachers, "teachers")}
        </p>
        <p>
          <span className="font-semibold">{t("adminSubscription.parents")}({userNames.parents.length}) </span>
          {renderUserList(userNames.parents, "parents")}
        </p>
        <p>
          <span className="font-semibold">{t("adminSubscription.students")}({userNames.students.length}) </span>
          {renderUserList(userNames.students, "students")}
        </p>
      </div>
    </div>
  );
};

export default Subscription;
